import PageHeading from "../../../components/desktop/PageHeading";
import React, { useEffect, useRef, useState } from "react";
import { FiCalendar } from "react-icons/fi";
import { toast } from "react-toastify";
import "./Shiftroster.css";
import { api } from "../../../utils/api";
import { useAuthStore } from "../../../store/authStore";
import { useLoaderStore } from "../../../store/loaderStore";
import UserSearchSelect from "../../../components/desktop/UserSearchSelect";
import ExcelJS from "exceljs";
import * as XLSX from "xlsx";

const formatInputDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const getCurrentMonthRange = () => {
  const today = new Date();

  return {
    from_date: formatInputDate(
      new Date(today.getFullYear(), today.getMonth(), 1),
    ),
    to_date: formatInputDate(
      new Date(today.getFullYear(), today.getMonth() + 1, 0),
    ),
  };
};

const getResponseList = (result) => {
  if (Array.isArray(result)) return result;
  if (Array.isArray(result?.data)) return result.data;
  return [];
};

const normalizeImportDate = (value) => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return formatInputDate(value);
  }

  if (typeof value === "number") {
    const parsed = XLSX.SSF.parse_date_code(value);
    if (parsed) {
      return `${parsed.y}-${String(parsed.m).padStart(2, "0")}-${String(
        parsed.d,
      ).padStart(2, "0")}`;
    }
  }

  const date = String(value || "").trim().split("T")[0];
  if (/^\d{4}-\d{2}-\d{2}$/.test(date)) return date;

  const dayFirstMatch = date.match(/^(\d{2})[-/](\d{2})[-/](\d{4})$/);
  if (dayFirstMatch) {
    return `${dayFirstMatch[3]}-${dayFirstMatch[2]}-${dayFirstMatch[1]}`;
  }

  return "";
};

export default function Shiftroster() {
  const { showLoader, hideLoader } = useLoaderStore();
  const { user } = useAuthStore();
  const companyId = user?.company_id;

  const [users, setUsers] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [shiftLoading, setShiftLoading] = useState(false);
  const [userShiftAssignments, setUserShiftAssignments] = useState([]);

  const [shiftFilter, setShiftFilter] = useState(getCurrentMonthRange);
  const [searchUser, setSearchUser] = useState("");
  const [selectedUsers, setSelectedUsers] = useState([]);
  // User filter captured when Search is pressed, like the Report page
  const [appliedUserFilter, setAppliedUserFilter] = useState({
    ids: [],
    query: "",
  });

  const shiftFromDateRef = useRef(null);
  const shiftToDateRef = useRef(null);
  const shiftAssignmentFromDateRefs = useRef({});
  const shiftAssignmentToDateRefs = useRef({});
  const shiftImportInputRef = useRef(null);
  const [isTemplateDownloading, setIsTemplateDownloading] = useState(false);
  const [showDownloadChoice, setShowDownloadChoice] = useState(false);
  const [isImportPreparing, setIsImportPreparing] = useState(false);
  const [isShiftImporting, setIsShiftImporting] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0 });

  const fetchUsers = async () => {
    if (!companyId) return;

    try {
      const response = await api.get(`/user/active-list/${companyId}`);
      const result = await response.json();
      const list = getResponseList(result);

      setUsers(list);
      return list;
    } catch (error) {
      console.error("FETCH USERS ERROR:", error);
      toast.error(error.message || "Failed to load usernames");
    }
  };

  // ── Active shifts for the dropdown ──
  const fetchShifts = async () => {
    if (!companyId) return;

    try {
      setShiftLoading(true);

      const response = await api.get(`/shift/active-list/${companyId}`);

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Failed to load shifts");
      }

      const list = getResponseList(result);
      setShifts(list);
      return list;
    } catch (error) {
      console.error(error);
      toast.error(error.message || "Failed to load shifts");
    } finally {
      setShiftLoading(false);
    }
  };

  const fetchShiftAssignments = async (
    fromDate = shiftFilter.from_date,
    toDate = shiftFilter.to_date,
  ) => {
    try {
      if (!companyId) return;

      if (!fromDate || !toDate) {
        const currentMonth = getCurrentMonthRange();
        fromDate = currentMonth.from_date;
        toDate = currentMonth.to_date;
      }

      if (new Date(fromDate) > new Date(toDate)) {
        toast.warning("From Date cannot be after To Date");
        return;
      }

      setShiftLoading(true);

      const response = await api.get(
        `/shift-assignment/list/${companyId}?from_date=${fromDate}&to_date=${toDate}`,
      );

      const result = await response.json();
      const assignments = getResponseList(result);

      const normalizedAssignments = assignments.map((assignment) => ({
        ...assignment,
        assignment_id: assignment.assignment_id ?? assignment.id ?? null,
        user_id: assignment.user_id ?? assignment.user?.id ?? "",
        username:
          assignment.username ??
          assignment.user_name ??
          assignment.employee_name ??
          assignment.user?.username ??
          [
            assignment.first_name ?? assignment.user?.first_name,
            assignment.last_name ?? assignment.user?.last_name,
          ]
            .filter(Boolean)
            .join(" "),
        shift_id: assignment.shift_id ?? assignment.shift?.id ?? "",
        from_date: assignment.from_date?.split("T")[0] ?? "",
        to_date: assignment.to_date?.split("T")[0] ?? "",
        isactive:
          assignment.isactive === true || Number(assignment.isactive) === 1
            ? 1
            : 0,
      }));

      setUserShiftAssignments(normalizedAssignments);
      return normalizedAssignments;
    } catch (error) {
      console.error("FETCH SHIFT ASSIGNMENTS ERROR:", error);

      setUserShiftAssignments([]);

      toast.error(error.message || "Failed to load shift assignments");
      
    } finally {
      setShiftLoading(false);
    }
  };

  const validateDateYear = (date, fieldName) => {
    if (!date) {
      toast.warning(`Please select ${fieldName}`);
      return false;
    }

    const year = date.split("-")[0];

    if (!/^\d{4}$/.test(year)) {
      toast.warning(`${fieldName} must have a 4-digit year`);
      return false;
    }

    return true;
  };

  const getUserName = (assignment) => {
    if (assignment.username) return assignment.username;

    const matchedUser = users.find(
      (employee) => Number(employee.id) === Number(assignment.user_id),
    );

    if (!matchedUser) return "-";

    const fullName = `${matchedUser.first_name || ""} ${
      matchedUser.last_name || ""
    }`.trim();

    return matchedUser.username || fullName || matchedUser.employee_code || "-";
  };

  const isUserFilterApplied =
    appliedUserFilter.ids.length > 0 || Boolean(appliedUserFilter.query);

  // Selected users win over typed text; the original index is kept so row
  // edits still update the right assignment.
  const filteredUserShiftAssignments = userShiftAssignments
    .map((assignment, index) => ({ assignment, index }))
    .filter(({ assignment }) => {
      if (appliedUserFilter.ids.length > 0) {
        return appliedUserFilter.ids.includes(Number(assignment.user_id));
      }

      if (!appliedUserFilter.query) return true;

      const matchedUser = users.find(
        (employee) => Number(employee.id) === Number(assignment.user_id),
      );

      return [
        getUserName(assignment),
        matchedUser?.first_name,
        matchedUser?.last_name,
        matchedUser?.employee_code,
        assignment.user_id,
      ].some((value) =>
        String(value || "")
          .toLowerCase()
          .includes(appliedUserFilter.query),
      );
    });

  const handleShiftFilter = async () => {
    if (!shiftFilter.from_date) {
      toast.warning("Please select From Date");
      return;
    }

    if (!shiftFilter.to_date) {
      toast.warning("Please select To Date");
      return;
    }

    if (new Date(shiftFilter.from_date) > new Date(shiftFilter.to_date)) {
      toast.warning("From Date cannot be after To Date");
      return;
    }

    try {
      showLoader("master");
      const assignments = await fetchShiftAssignments(
        shiftFilter.from_date,
        shiftFilter.to_date,
      );

      if (assignments && selectedUsers.length > 0) {
        const assignedUserIds = new Set(
          assignments.map((assignment) => Number(assignment.user_id)),
        );
        const unassignedNames = selectedUsers
          .filter((u) => !assignedUserIds.has(Number(u.id)))
          .map((u) => u.name || u.employee_code || u.id);

        if (unassignedNames.length > 0) {
          toast.warning(
            `${unassignedNames.join(", ")} ${
              unassignedNames.length === 1 ? "is" : "are"
            } not assigned a shift for the selected dates`,
          );
        }
      }

      setAppliedUserFilter({
        ids: selectedUsers.map((u) => Number(u.id)),
        query:
          selectedUsers.length > 0 ? "" : searchUser.trim().toLowerCase(),
      });
    } finally {
      hideLoader();
    }
  };

  const downloadShiftTemplate = async (includeAssignmentData = false) => {
    if (!companyId) return;

    try {
      setIsTemplateDownloading(true);
      showLoader("master");

      const [assignmentResponse, userResponse, shiftResponse] =
        await Promise.all([
          api.get(`/shift-assignment/list/${companyId}`),
          api.get(`/user/active-list/${companyId}`),
          api.get(`/shift/active-list/${companyId}`),
        ]);

      const [assignmentResult, userResult, shiftResult] = await Promise.all([
        assignmentResponse.json(),
        userResponse.json(),
        shiftResponse.json(),
      ]);

      const assignments = getResponseList(assignmentResult);
      const activeUsers = getResponseList(userResult);
      const activeShifts = getResponseList(shiftResult);
      const workbook = new ExcelJS.Workbook();
      const assignmentSheet = workbook.addWorksheet("Shift Assignments");
      const userSheet = workbook.addWorksheet("Users");
      const shiftSheet = workbook.addWorksheet("Shifts");

      assignmentSheet.addRow(["user_id", "shift_id", "from_date", "to_date"]);
      if (includeAssignmentData) assignments.forEach((assignment) => {
        const userId = assignment.user_id ?? assignment.user?.id ?? "";
        const shiftId = assignment.shift_id ?? assignment.shift?.id ?? "";
        const matchedUser = activeUsers.find(
          (employee) =>
            Number(employee.id ?? employee.user_id) === Number(userId),
        );
        const matchedShift = activeShifts.find(
          (shift) =>
            Number(shift.id ?? shift.shift_id) === Number(shiftId),
        );
        const userName = matchedUser
          ? matchedUser.username ||
            `${matchedUser.first_name || ""} ${
              matchedUser.last_name || ""
            }`.trim() ||
            matchedUser.employee_code ||
            ""
          : assignment.username || assignment.user_name || "";
        const shiftName = matchedShift
          ? matchedShift.shift_name || matchedShift.name || ""
          : assignment.shift_name || assignment.shift?.shift_name || "";

        assignmentSheet.addRow([
          userName ? `${userId} | ${userName}` : userId,
          shiftName ? `${shiftId} | ${shiftName}` : shiftId,
          assignment.from_date?.split("T")[0] ?? "",
          assignment.to_date?.split("T")[0] ?? "",
        ]);
      });

      userSheet.addRow([
        "user_id",
        "name",
        "department",
        "designation",
        "Dropdown Value",
      ]);
      activeUsers.forEach((employee) => {
        const userId = employee.id ?? employee.user_id ?? "";
        const name =
          employee.username ||
          `${employee.first_name || ""} ${employee.last_name || ""}`.trim() ||
          employee.employee_code ||
          "";

        userSheet.addRow([
          userId,
          name,
          employee.department_name ??
            employee.department?.department_name ??
            employee.department?.name ??
            employee.department ??
            "",
          employee.designation_name ??
            employee.designation?.designation_name ??
            employee.designation?.name ??
            employee.designation ??
            "",
          `${userId} | ${name}`,
        ]);
      });

      shiftSheet.addRow([
        "shift_id",
        "shift_name",
        "start_time",
        "end_time",
        "Dropdown Value",
      ]);
      activeShifts.forEach((shift) => {
        const shiftId = shift.id ?? shift.shift_id ?? "";
        const shiftName = shift.shift_name ?? shift.name ?? "";

        shiftSheet.addRow([
          shiftId,
          shiftName,
          shift.shift_start_time ?? shift.start_time ?? "",
          shift.shift_end_time ?? shift.end_time ?? "",
          `${shiftId} | ${shiftName}`,
        ]);
      });

      [assignmentSheet, userSheet, shiftSheet].forEach((sheet) => {
        sheet.getRow(1).font = { bold: true, color: { argb: "FFFFFFFF" } };
        sheet.getRow(1).fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FF3867D6" },
        };
        sheet.views = [{ state: "frozen", ySplit: 1 }];
        sheet.columns.forEach((column) => {
          column.width = 22;
        });
      });

      userSheet.getColumn(5).hidden = true;
      shiftSheet.getColumn(5).hidden = true;

      const userOptionEnd = Math.max(activeUsers.length + 1, 2);
      const shiftOptionEnd = Math.max(activeShifts.length + 1, 2);
      workbook.definedNames.add(
        `Users!$E$2:$E$${userOptionEnd}`,
        "ShiftRosterUserOptions",
      );
      workbook.definedNames.add(
        `Shifts!$E$2:$E$${shiftOptionEnd}`,
        "ShiftRosterShiftOptions",
      );

      const dropdownValidation = (rangeName) => ({
        type: "list",
        allowBlank: false,
        formulae: [rangeName],
        showErrorMessage: true,
        errorTitle: "Select a listed value",
        error: "Please choose a value from the drop-down list.",
      });

      assignmentSheet.dataValidations.add(
        "A2:A501",
        dropdownValidation("ShiftRosterUserOptions"),
      );
      assignmentSheet.dataValidations.add(
        "B2:B501",
        dropdownValidation("ShiftRosterShiftOptions"),
      );

      const fileData = await workbook.xlsx.writeBuffer();
      const fileUrl = URL.createObjectURL(
        new Blob([fileData], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
      );
      const link = document.createElement("a");
      link.href = fileUrl;
      link.download = includeAssignmentData
        ? "Shift_Assignments_With_Data.xlsx"
        : "Shift_Assignment_Import_Template.xlsx";
      link.click();
      URL.revokeObjectURL(fileUrl);

      toast.success(
        includeAssignmentData
          ? "Shift assignments downloaded with data"
          : "Blank shift assignment template downloaded",
      );
    } catch (error) {
      console.error("DOWNLOAD SHIFT TEMPLATE ERROR:", error);
      toast.error(error.message || "Unable to download shift template");
    } finally {
      hideLoader();
      setIsTemplateDownloading(false);
      setShowDownloadChoice(false);
    }
  };

  const handleImportShifts = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !companyId) return;

    try {
      setIsImportPreparing(true);
      showLoader("master");

      const [userResponse, shiftResponse] = await Promise.all([
        api.get(`/user/active-list/${companyId}`),
        api.get(`/shift/active-list/${companyId}`),
      ]);
      const [userResult, shiftResult] = await Promise.all([
        userResponse.json(),
        shiftResponse.json(),
      ]);
      const activeUsers = getResponseList(userResult);
      const activeShifts = getResponseList(shiftResult);

      const fileData = await file.arrayBuffer();
      const workbook = XLSX.read(fileData, {
        type: "array",
        cellDates: true,
      });
      const assignmentSheet =
        workbook.Sheets["Shift Assignments"] ||
        workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(assignmentSheet, { defval: "" });

      if (!rows.length) {
        throw new Error("The Shift Assignments sheet has no rows to import");
      }

      const invalidRows = [];
      const validRows = [];

      rows.forEach((row, index) => {
        const rowNumber = index + 2;
        const userId = Number(
          String(row.user_id || "")
            .split("|")[0]
            .trim(),
        );
        const shiftId = Number(
          String(row.shift_id ?? row.shiftid ?? "")
            .split("|")[0]
            .trim(),
        );
        const fromDate = normalizeImportDate(row.from_date ?? row.from);
        const toDate = normalizeImportDate(row.to_date ?? row.to);
        const matchedUser = activeUsers.find(
          (employee) =>
            Number(employee.id ?? employee.user_id) === Number(userId),
        );
        const matchedShift = activeShifts.find(
          (shift) =>
            Number(shift.id ?? shift.shift_id) === Number(shiftId),
        );
        const reasons = [];

        if (!Number.isInteger(userId) || userId <= 0 || !matchedUser) {
          reasons.push("Select a valid active user");
        }
        if (!Number.isInteger(shiftId) || shiftId <= 0 || !matchedShift) {
          reasons.push("Select a valid active shift");
        }
        if (!fromDate) reasons.push("From Date is invalid");
        if (!toDate) reasons.push("To Date is invalid");
        if (fromDate && toDate && new Date(fromDate) > new Date(toDate)) {
          reasons.push("From Date cannot be after To Date");
        }

        if (reasons.length) {
          invalidRows.push({ rowNumber, reason: reasons.join("; ") });
          return;
        }

        const userName =
          matchedUser.username ||
          `${matchedUser.first_name || ""} ${
            matchedUser.last_name || ""
          }`.trim() ||
          matchedUser.employee_code ||
          "-";
        const shiftName =
          matchedShift.shift_name || matchedShift.name || "-";
        const payload = {
          company_id: Number(companyId),
          user_id: userId,
          shift_id: shiftId,
          from_date: fromDate,
          to_date: toDate,
        };

        validRows.push({
          rowNumber,
          payload,
          userLabel: `${userId} | ${userName}`,
          shiftLabel: `${shiftId} | ${shiftName}`,
        });
      });

      setImportPreview({ validRows, invalidRows });
      if (!validRows.length) {
        toast.error("No valid shift assignments were found in the file");
      }
    } catch (error) {
      console.error("PREPARE SHIFT IMPORT ERROR:", error);
      toast.error(error.message || "Unable to prepare shift import");
    } finally {
      hideLoader();
      setIsImportPreparing(false);
    }
  };

  const confirmShiftImport = async () => {
    if (!importPreview?.validRows.length) return;

    const payload = importPreview.validRows.map((row) => row.payload);

    try {
      setIsShiftImporting(true);
      setImportProgress({ current: 0, total: payload.length });
      showLoader("master");

      const response = await api.post(`/shift-assignment/bulk-add`, payload);
      const result = await response.json();

      if (result?.status === false) {
        throw new Error(result.message || "Shift import was rejected");
      }

      setImportProgress({ current: payload.length, total: payload.length });
      toast.success(
        `${payload.length} shift assignment${
          payload.length === 1 ? "" : "s"
        } imported successfully`,
      );
      await fetchShiftAssignments();
      setImportPreview(null);
    } catch (error) {
      console.error("IMPORT SHIFT ASSIGNMENTS ERROR:", error);
      toast.error(error.message || "Unable to import shift assignments");
    } finally {
      hideLoader();
      setIsShiftImporting(false);
    }
  };

  const handleShiftRowSave = async (assignment) => {
    // Validation
    if (!assignment.user_id) {
      toast.warning("Please select username");
      return;
    }

    if (!assignment.shift_id) {
      toast.warning("Please select shift");
      return;
    }

    if (!validateDateYear(assignment.from_date, "From Date")) {
      return;
    }

    if (!validateDateYear(assignment.to_date, "To Date")) {
      return;
    }

    if (new Date(assignment.from_date) > new Date(assignment.to_date)) {
      toast.warning("From Date cannot be after To Date");
      return;
    }

    try {
      showLoader("master");

      const payload = {
        company_id: Number(companyId),
        user_id: Number(assignment.user_id),
        shift_id: Number(assignment.shift_id),
        from_date: assignment.from_date,
        to_date: assignment.to_date,
        isactive: Number(assignment.isactive) === 1,
      };

      const response = await api.put(
        `/shift-assignment/update/${assignment.assignment_id}`,
        payload,
      );

      const result = await response.json();

      if (result.status) {
        toast.success(
          "Shift assignment updated successfully",
        );

        // Reload assignments
        await fetchShiftAssignments();
      } else {
        toast.error(result.message || "Failed to save shift assignment");
      }
    } catch (error) {
      console.error("SHIFT ASSIGNMENT SAVE ERROR:", error);

      toast.error(error.message || "Failed to save shift assignment");
    } finally {
      hideLoader();
    }
  };

  const handleShiftStatusToggle = async (assignment, checked) => {
    try {
      showLoader("master");

      const payload = {
        company_id: Number(companyId),
        user_id: Number(assignment.user_id),
        shift_id: Number(assignment.shift_id),
        from_date: assignment.from_date,
        to_date: assignment.to_date,
        isactive: checked,
      };

      const response = await api.put(
        `/shift-assignment/update/${assignment.assignment_id}`,
        payload,
      );

      const result = await response.json();

      if (!response.ok || !result.status) {
        throw new Error(result.message || "Failed to update shift status");
      }

      // Update UI immediately
      setUserShiftAssignments((prev) =>
        prev.map((item) =>
          item.assignment_id === assignment.assignment_id
            ? {
                ...item,
                isactive: checked ? 1 : 0,
              }
            : item,
        ),
      );

      toast.success(
        checked
          ? "Shift assignment activated successfully"
          : "Shift assignment deactivated successfully",
      );
    } catch (error) {
      console.error("SHIFT STATUS UPDATE ERROR:", error);

      // Keep the toggle unchanged if API fails
      setUserShiftAssignments((prev) =>
        prev.map((item) =>
          item.assignment_id === assignment.assignment_id
            ? {
                ...item,
                isactive: checked ? 0 : 1,
              }
            : item,
        ),
      );

      toast.error(error.message || "Failed to update shift status");
    } finally {
      hideLoader();
    }
  };

  useEffect(() => {
    if (companyId) {
      const loadShiftRoster = async () => {
        try {
          showLoader("master");
          await Promise.all([
            fetchUsers(),
            fetchShifts(),
            fetchShiftAssignments(),
          ]);
        } finally {
          hideLoader();
        }
      };

      loadShiftRoster();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  return (
    <div className="shift-roster-page-container">
      <PageHeading>Shift Roster</PageHeading>
      {showDownloadChoice && (
        <div className="shift-roster-page-download-backdrop" role="dialog" aria-modal="true">
          <div className="shift-roster-page-download-modal">
            <h3>Download Shift Assignment Template</h3>
            <p>
              Choose whether to download a blank template or include existing
              shift assignments.
            </p>

            <div className="shift-roster-page-download-actions">
              <button
                type="button"
                className="shift-roster-page-action-btn"
                disabled={isTemplateDownloading}
                onClick={() => downloadShiftTemplate(false)}
              >
                {isTemplateDownloading ? "Preparing..." : "Without Data"}
              </button>

              <button
                type="button"
                className="shift-roster-page-action-btn shift-roster-page-import-btn"
                disabled={isTemplateDownloading}
                onClick={() => downloadShiftTemplate(true)}
              >
                {isTemplateDownloading ? "Preparing..." : "With Data"}
              </button>
            </div>

            <button
              type="button"
              className="shift-roster-page-download-cancel"
              disabled={isTemplateDownloading}
              onClick={() => setShowDownloadChoice(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {importPreview ? (
        <div className="shift-roster-page-preview-container">
          <div className="shift-roster-page-preview-header">
            <div>
              <h3>Review Shift Assignment Import</h3>
              <p>
                No assignments have been created yet. Confirm to import the
                valid rows below.
              </p>
            </div>

            <div className="shift-roster-page-preview-summary">
              <span className="shift-roster-page-valid-count">
                {importPreview.validRows.length} ready
              </span>
              <span className="shift-roster-page-invalid-count">
                {importPreview.invalidRows.length} invalid
              </span>
            </div>
          </div>

          {isShiftImporting && (
            <div className="shift-roster-page-progress" aria-live="polite">
              <div className="shift-roster-page-progress-label">
                <span>
                  Importing assignments: {importProgress.current} of{" "}
                  {importProgress.total}
                </span>
                <strong>
                  {importProgress.total
                    ? Math.round(
                        (importProgress.current / importProgress.total) * 100,
                      )
                    : 0}
                  %
                </strong>
              </div>

              <div className="shift-roster-page-progress-track">
                <div
                  className="shift-roster-page-progress-fill"
                  style={{
                    width: `${
                      importProgress.total
                        ? (importProgress.current / importProgress.total) * 100
                        : 0
                    }%`,
                  }}
                />
              </div>
            </div>
          )}

          <div className="shift-roster-page-preview-table-wrapper">
            <table className="shift-roster-page-preview-table">
              <thead>
                <tr>
                  <th>Excel Row</th>
                  <th>User</th>
                  <th>Shift</th>
                  <th>From Date</th>
                  <th>To Date</th>
                </tr>
              </thead>

              <tbody>
                {importPreview.validRows.map(
                  ({ rowNumber, payload, userLabel, shiftLabel }) => (
                    <tr key={rowNumber}>
                      <td>{rowNumber}</td>
                      <td>{userLabel}</td>
                      <td>{shiftLabel}</td>
                      <td>{payload.from_date}</td>
                      <td>{payload.to_date}</td>
                    </tr>
                  ),
                )}

                {importPreview.invalidRows.map(({ rowNumber, reason }) => (
                  <tr
                    key={`invalid-${rowNumber}`}
                    className="shift-roster-page-invalid-row"
                  >
                    <td>{rowNumber}</td>
                    <td colSpan="4">Not ready: {reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="shift-roster-page-preview-actions">
            <button
              type="button"
              className="shift-roster-page-import-cancel-btn"
              disabled={isShiftImporting}
              onClick={() => setImportPreview(null)}
            >
              Cancel
            </button>

            <button
              type="button"
              className="shift-roster-page-import-confirm-btn"
              disabled={!importPreview.validRows.length || isShiftImporting}
              onClick={confirmShiftImport}
            >
              {isShiftImporting ? "Importing Shifts..." : "Confirm Import"}
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* ===== SHIFT ROSTER ASSIGNMENTS ===== */}
          <div className="shift-roster-page-assignment-section">
        <div className="shift-roster-page-table-header">
            {/* LEFT SIDE - DATE FILTER */}
            <div className="shift-roster-page-filter">
              <div className="shift-roster-page-filter-field">
                <label>From Date</label>

                <div className="shift-roster-page-searchbar-date-wrapper">
                  <input
                    type="text"
                    className="shift-roster-page-searchbar-date-display"
                    value={
                      shiftFilter.from_date
                        ? shiftFilter.from_date.split("-").reverse().join("-")
                        : ""
                    }
                    placeholder="dd-mm-yyyy"
                    readOnly
                    onClick={() => {
                      shiftFromDateRef.current?.showPicker();
                    }}
                  />

                  <FiCalendar
                    className="shift-roster-page-searchbar-calendar-icon"
                    onClick={() => {
                      shiftFromDateRef.current?.showPicker();
                    }}
                  />

                  <input
                    ref={shiftFromDateRef}
                    type="date"
                    className="shift-roster-page-searchbar-date-picker"
                    min="2000-01-01"
                    max="2099-12-31"
                    value={shiftFilter.from_date}
                    onChange={(e) =>
                      setShiftFilter((prev) => ({
                        ...prev,
                        from_date: e.target.value,
                      }))
                    }
                  />
                </div>
              </div>

              <div className="shift-roster-page-filter-field">
                <label>To Date</label>

                <div className="shift-roster-page-searchbar-date-wrapper">
                  <input
                    type="text"
                    className="shift-roster-page-searchbar-date-display"
                    value={
                      shiftFilter.to_date
                        ? shiftFilter.to_date.split("-").reverse().join("-")
                        : ""
                    }
                    placeholder="dd-mm-yyyy"
                    readOnly
                    onClick={() => {
                      shiftToDateRef.current?.showPicker();
                    }}
                  />

                  <FiCalendar
                    className="shift-roster-page-searchbar-calendar-icon"
                    onClick={() => {
                      shiftToDateRef.current?.showPicker();
                    }}
                  />

                  <input
                    ref={shiftToDateRef}
                    type="date"
                    className="shift-roster-page-searchbar-date-picker"
                    min="2000-01-01"
                    max="2099-12-31"
                    value={shiftFilter.to_date}
                    onChange={(e) =>
                      setShiftFilter((prev) => ({
                        ...prev,
                        to_date: e.target.value,
                      }))
                    }
                  />
                </div>
              </div>

              <UserSearchSelect
                users={users}
                searchText={searchUser}
                onSearchTextChange={setSearchUser}
                selectedUsers={selectedUsers}
                onSelectedUsersChange={setSelectedUsers}
                onEnter={handleShiftFilter}
              />

              <button
                type="button"
                className="shift-roster-page-filter-btn"
                onClick={handleShiftFilter}
              >
                Search
              </button>
            </div>

            <div className="shift-roster-page-filter-actions">
              <button
                type="button"
                className="shift-roster-page-action-btn"
                disabled={isTemplateDownloading}
                onClick={() => setShowDownloadChoice(true)}
              >
                Download Template
              </button>

              <button
                type="button"
                className="shift-roster-page-action-btn shift-roster-page-import-btn"
                disabled={isImportPreparing}
                onClick={() => shiftImportInputRef.current?.click()}
              >
                {isImportPreparing ? "Preparing Import..." : "Import Shift"}
              </button>

              <input
                ref={shiftImportInputRef}
                type="file"
                accept=".xlsx,.xls"
                className="shift-roster-page-import-file-input"
                onChange={handleImportShifts}
              />
            </div>
          </div>

          <div className="shift-roster-page-table-wrapper">
            <table className="shift-roster-page-table">
              <thead>
                <tr>
                  <th>Sr. No.</th>
                  <th>Username</th>
                  <th>Shift Name</th>
                  <th>From Date</th>
                  <th>To Date</th>
                  <th className="shift-roster-page-status-col">Status</th>
                  <th className="shift-roster-page-action-col">Action</th>
                </tr>
              </thead>

              <tbody>
                {shiftLoading ? (
                  <tr>
                    <td colSpan="7" className="shift-roster-page-no-data">
                      Loading...
                    </td>
                  </tr>
                ) : filteredUserShiftAssignments.length > 0 ? (
                  filteredUserShiftAssignments.map(({ assignment, index }, rowIndex) => (
                    <tr key={assignment.assignment_id || `new-${index}`}>
                      {/* Sr. No. */}
                      <td className="shift-roster-page-serial-col">{rowIndex + 1}</td>

                      {/* Username */}
                      <td className="shift-roster-page-username-col">
                        {getUserName(assignment)}
                      </td>

                      {/* Shift Name */}
                      <td>
                        <select
                          value={assignment.shift_id || ""}
                          onChange={(e) => {
                            const updated = [...userShiftAssignments];

                            updated[index] = {
                              ...updated[index],
                              shift_id: e.target.value
                                ? Number(e.target.value)
                                : "",
                            };

                            setUserShiftAssignments(updated);
                          }}
                        >
                          <option value="">Select Shift</option>

                          {shifts.map((shift) => (
                            <option key={shift.id} value={String(shift.id)}>
                              {shift.shift_name}
                            </option>
                          ))}
                        </select>
                      </td>

                      {/* From Date */}
                      <td>
                        <div className="shift-roster-page-assignment-date-wrapper">
                          <input
                            type="text"
                            className="shift-roster-page-assignment-date-display"
                            value={
                              assignment.from_date
                                ? assignment.from_date
                                    .split("-")
                                    .reverse()
                                    .join("-")
                                : ""
                            }
                            placeholder="dd-mm-yyyy"
                            readOnly
                            onClick={() => {
                              shiftAssignmentFromDateRefs.current[
                                index
                              ]?.showPicker();
                            }}
                          />

                          <FiCalendar
                            className="shift-roster-page-assignment-calendar-icon"
                            onClick={() => {
                              shiftAssignmentFromDateRefs.current[
                                index
                              ]?.showPicker();
                            }}
                          />

                          <input
                            ref={(el) => {
                              shiftAssignmentFromDateRefs.current[index] = el;
                            }}
                            type="date"
                            className="shift-roster-page-assignment-date-picker"
                            min="2000-01-01"
                            max="2099-12-31"
                            value={assignment.from_date || ""}
                            onChange={(e) => {
                              const updated = [...userShiftAssignments];

                              updated[index] = {
                                ...updated[index],
                                from_date: e.target.value,
                              };

                              setUserShiftAssignments(updated);
                            }}
                          />
                        </div>
                      </td>

                      {/* To Date */}
                      <td>
                        <div className="shift-roster-page-assignment-date-wrapper">
                          <input
                            type="text"
                            className="shift-roster-page-assignment-date-display"
                            value={
                              assignment.to_date
                                ? assignment.to_date
                                    .split("-")
                                    .reverse()
                                    .join("-")
                                : ""
                            }
                            placeholder="dd-mm-yyyy"
                            readOnly
                            onClick={() => {
                              shiftAssignmentToDateRefs.current[
                                index
                              ]?.showPicker();
                            }}
                          />

                          <FiCalendar
                            className="shift-roster-page-assignment-calendar-icon"
                            onClick={() => {
                              shiftAssignmentToDateRefs.current[
                                index
                              ]?.showPicker();
                            }}
                          />

                          <input
                            ref={(el) => {
                              shiftAssignmentToDateRefs.current[index] = el;
                            }}
                            type="date"
                            className="shift-roster-page-assignment-date-picker"
                            min="2000-01-01"
                            max="2099-12-31"
                            value={assignment.to_date || ""}
                            onChange={(e) => {
                              const updated = [...userShiftAssignments];

                              updated[index] = {
                                ...updated[index],
                                to_date: e.target.value,
                              };

                              setUserShiftAssignments(updated);
                            }}
                          />
                        </div>
                      </td>

                      {/* Status */}
                      <td className="shift-roster-page-status-col">
                        <label className="shift-roster-page-toggle">
                          <input
                            type="checkbox"
                            checked={Number(assignment.isactive) === 1}
                            onChange={(e) =>
                              handleShiftStatusToggle(
                                assignment,
                                e.target.checked,
                              )
                            }
                          />

                          <span className="shift-roster-page-toggle-slider"></span>
                        </label>
                      </td>

                      {/* Action */}
                      <td className="shift-roster-page-action-col">
                        <button
                          type="button"
                          className="shift-roster-page-save-btn"
                          onClick={() => handleShiftRowSave(assignment)}
                        >
                          Save
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="shift-roster-page-no-data">
                      {isUserFilterApplied
                        ? "No matching users found."
                        : "No shift assignments found."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          </div>
        </>
      )}
    </div>
  );
}
